// The site's two colour themes (docs/plans/dark-mode.md): dark, the default, and light, picked with the header's button
// and remembered by this browser. The head script (build/render.ts) applies the remembered theme before the page is
// painted; startTheme() then keeps the button, the page, other tabs and pages restored from the back-forward cache in
// step. Nothing here touches the browser until it is called, so the build imports the shared rules from here.
import { lang, translate } from '../i18n/index.ts';

export type Theme = 'dark' | 'light';

/** Where this browser remembers the choice: exactly "dark" or "light". */
export const THEME_KEY = 'chordToMidi.theme';

/** The browser's toolbar colour in each theme (<meta name="theme-color">): the tool window's colour. */
export const THEME_COLORS: Record<Theme, string> = { dark: '#16181B', light: '#F3F9FD' };

/** A remembered value as a theme: light only when it says exactly that. Nothing, or anything else, is dark. */
export function themeOf(value: string | null | undefined): Theme {
  return value === 'light' ? 'light' : 'dark';
}

/** The theme the page shows now. */
export function currentTheme(): Theme {
  return themeOf(document.documentElement.getAttribute('data-theme'));
}

const listeners = new Set<(theme: Theme) => void>();

/** Calls `listener` after every change of the page's theme (the piano roll redraws). Returns the unsubscribe. */
export function onThemeChanged(listener: (theme: Theme) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Shows `theme`: the root's data-theme, the browser's colour scheme and toolbar colour, and the button's state. */
export function applyTheme(theme: Theme): void {
  const changed = currentTheme() !== theme;
  document.documentElement.setAttribute('data-theme', theme);
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLORS[theme]);
  for (const button of document.querySelectorAll<HTMLButtonElement>('.theme-toggle')) {
    // The name stays "Dark mode"; pressed says whether it is on. The tooltip says what a press does.
    button.setAttribute('aria-pressed', String(theme === 'dark'));
    button.title = translate(lang, theme === 'dark' ? 'site.theme.toLight' : 'site.theme.toDark');
  }
  if (changed) for (const listener of listeners) listener(theme);
}

/** The theme this browser remembers; null when its storage can't be read (blocked site data). */
function rememberedTheme(): Theme | null {
  try {
    return themeOf(localStorage.getItem(THEME_KEY));
  } catch {
    return null;
  }
}

function rememberTheme(theme: Theme) {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Not remembered (blocked site data): this page still switches.
  }
}

function isLocalStorage(area: Storage | null): boolean {
  try {
    return area === localStorage;
  } catch {
    return false;
  }
}

let started = false;

/** Starts the header's theme button, and follows a choice made in another tab or a page restored from the cache. */
export function startTheme(): void {
  if (started) return;
  started = true;
  // The head script picked the theme; the button learns it.
  applyTheme(currentTheme());
  for (const button of document.querySelectorAll<HTMLButtonElement>('.theme-toggle')) {
    button.addEventListener('click', () => {
      const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
      applyTheme(next);
      rememberTheme(next);
    });
  }
  // Another tab chose, or removed the choice (dark). Shown here, never written back.
  window.addEventListener('storage', (event) => {
    if (event.key === THEME_KEY) applyTheme(themeOf(event.newValue));
    else if (event.key === null && isLocalStorage(event.storageArea)) applyTheme('dark'); // the site's storage cleared
  });
  // Back from the back-forward cache: the latest choice wins, unless storage can't be read.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const theme = rememberedTheme();
    if (theme) applyTheme(theme);
  });
}
